import { useTranslation } from 'react-i18next'
import type { Conflict, MergeResult } from '@/services/merge'
import type { Choice, Resolution } from '@/services/merge/resolve'
import { ConflictRow } from './ConflictRow'

interface ConflictListProps {
  mergeResult: MergeResult
  resolutions: Record<string, Resolution>
  onChoose: (conflictId: string, choice: Choice) => void
  onFieldChoose: (conflictId: string, field: string, choice: Choice) => void
}

interface ConflictGroup {
  tableLabel: string
  tableUid: string | null
  conflicts: Conflict[]
}

function groupConflicts(conflicts: Conflict[]): ConflictGroup[] {
  const groups = new Map<string | null, ConflictGroup>()

  for (const c of conflicts) {
    const groupKey = c.parentUid ?? (c.entityKind === 'column' ? c.uid : null) ?? c.uid
    const tableUid = c.parentUid ?? (c.entityKind === 'table' ? c.uid : null)
    const label = tableUid
      ? `Tabela "${(c.parentUid ? (c.mineSnapshot as Record<string, unknown>)?.['name'] : (c.mineSnapshot as Record<string, unknown>)?.['name']) ?? tableUid}"`
      : c.entityKind === 'relationship'
        ? 'Relacionamentos'
        : c.entityKind === 'group'
          ? 'Grupos'
          : c.label

    if (!groups.has(groupKey)) {
      groups.set(groupKey, { tableLabel: String(label), tableUid, conflicts: [] })
    }
    groups.get(groupKey)!.conflicts.push(c)
  }

  return [...groups.values()]
}

export function ConflictList({ mergeResult, resolutions, onChoose, onFieldChoose }: ConflictListProps) {
  const { t } = useTranslation()
  const groups = groupConflicts(mergeResult.conflicts)

  if (mergeResult.conflicts.length === 0) {
    return (
      <div className="text-center py-8 text-[12px]" style={{ color: 'var(--text-dim)' }}>
        {t('conflict.noConflicts', 'Sem conflitos — merge automático aplicado.')}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Auto-merge summary */}
      {mergeResult.stats.autoMerged > 0 && (
        <div
          className="rounded-lg px-3 py-2 text-[11px]"
          style={{ background: 'color-mix(in srgb, var(--accent) 8%, transparent)', color: 'var(--text-muted)' }}
        >
          {t('conflict.autoMerged', '{count} mudanças aplicadas automaticamente.', {
            count: mergeResult.stats.autoMerged,
          })}
        </div>
      )}

      {/* Conflict groups */}
      {groups.map((group) => (
        <div key={group.tableUid ?? group.tableLabel}>
          <div
            className="text-[10px] font-semibold uppercase mb-2 px-0.5"
            style={{ color: 'var(--text-muted)' }}
          >
            {group.tableLabel}
          </div>
          <div className="space-y-2">
            {group.conflicts.map((c) => (
              <ConflictRow
                key={c.id}
                conflict={c}
                resolution={resolutions[c.id]}
                onChoose={onChoose}
                onFieldChoose={onFieldChoose}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
